import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useIsAdmin } from '../auth/AuthContext';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { listPublishedBlogs, listAllBlogs, type BlogListRow } from '../lib/blogsApi';
import './Blog.css';

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Public blog index. Admins additionally see drafts (via the admin list) plus a
// "New post" launcher and draft badges. Works logged-out (shared links/crawlers).
const BlogListPage: React.FC = () => {
  useDocumentTitle('blog');
  const navigate = useNavigate();
  const { state } = useAuth();
  const isAdmin = useIsAdmin();
  const token = state.kind === 'authenticated' ? state.idToken : undefined;

  const [rows, setRows] = useState<BlogListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = isAdmin ? await listAllBlogs(token) : await listPublishedBlogs();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load posts.');
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, token]);

  return (
    <div className="blog-page">
      <div className="blog-list-head">
        <h1 className="blog-list-title">blog</h1>
        {isAdmin && (
          <button className="blog-btn primary" onClick={() => navigate('/blogs/new')}>＋ New post</button>
        )}
      </div>

      {error && <div className="blog-error">{error}</div>}
      {!rows && !error && <div className="blog-muted">Loading…</div>}
      {rows && rows.length === 0 && <div className="blog-muted">No posts yet.</div>}

      <div className="blog-card-grid">
        {rows?.map(post => (
          <Link key={post.slug} to={`/blogs/${post.slug}`} className="blog-card">
            {post.cover_image_url && (
              <div className="blog-card-cover" style={{ backgroundImage: `url(${post.cover_image_url})` }} />
            )}
            <div className="blog-card-body">
              {/* Topic chips (first tags) — the article's subject at a glance. */}
              {post.tags && post.tags.length > 0 && (
                <div className="blog-card-topics">
                  {post.tags.slice(0, 3).map(t => <span key={t} className="blog-topic-chip">{t}</span>)}
                </div>
              )}
              <h2 className="blog-card-title">{post.title}</h2>
              <p className="blog-card-summary">{post.summary}</p>
              {/* Author alias · date · rough read-time. */}
              <div className="blog-card-meta">
                {post.author_handle && <span>by {post.author_handle}</span>}
                <span>{formatDate(post.published_at || post.created_at)}</span>
                {post.read_minutes > 0 && <span>{post.read_minutes} min read</span>}
                {post.status === 'draft' && <span className="blog-draft-badge">draft</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default BlogListPage;
